import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Input } from '../common';
import { CharacterAspects } from './CharacterAspects';
import { useUploadMedia } from '../../hooks/useSubmissions';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

const getMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/uploads/')) {
    return `${API_URL}${url}`;
  }
  return url;
};

export const CharacterModal = ({
  isOpen,
  onClose,
  gameId,
  character = null,
  onSave,
  onDelete,
  isSaving = false,
}) => {
  const isEditing = !!character;
  const fileInputRef = useRef(null);
  const uploadMedia = useUploadMedia(gameId);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
    aspects: [],
  });

  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Reset form when modal opens/closes or character changes
  useEffect(() => {
    if (isOpen) {
      if (character) {
        setFormData({
          name: character.name || '',
          description: character.description || '',
          imageUrl: character.imageUrl || '',
          aspects: character.aspects || [],
        });
        // If character has an image, show it as "uploaded"
        if (character.imageUrl) {
          setUploadedFile({ url: character.imageUrl, name: 'Current image' });
        } else {
          setUploadedFile(null);
        }
      } else {
        setFormData({
          name: '',
          description: '',
          imageUrl: '',
          aspects: [],
        });
        setUploadedFile(null);
      }
      setConfirmDelete(false);
      setUploadError(null);
    }
  }, [isOpen, character]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleAddAspect = (aspect) => {
    setFormData((prev) => ({
      ...prev,
      aspects: [...prev.aspects, aspect],
    }));
  };

  const handleRemoveAspect = (index) => {
    setFormData((prev) => ({
      ...prev,
      aspects: prev.aspects.filter((_, i) => i !== index),
    }));
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadError(null);

    // Validate type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setUploadError(`Invalid file type. Allowed: JPEG, PNG, GIF, WebP`);
      return;
    }

    // Validate size
    if (file.size > MAX_IMAGE_SIZE) {
      setUploadError(`File too large. Maximum: 5MB`);
      return;
    }

    try {
      const result = await uploadMedia.mutateAsync({ file, type: 'image' });
      setUploadedFile({
        name: file.name,
        url: result.media.url,
      });
      setFormData((prev) => ({ ...prev, imageUrl: result.media.url }));
    } catch (err) {
      setUploadError('Upload failed. Please try again.');
      console.error('Upload failed:', err);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    setUploadedFile(null);
    setFormData((prev) => ({ ...prev, imageUrl: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSave(formData);
  };

  const handleDelete = async () => {
    if (confirmDelete) {
      await onDelete(character.id || character._id);
      onClose();
    } else {
      setConfirmDelete(true);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Character' : 'Create Character'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-4">
            <Input
              label="Name"
              value={formData.name}
              onChange={handleChange('name')}
              placeholder="Character name"
              required
              maxLength={100}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={handleChange('description')}
                placeholder="Brief description of the character"
                rows={4}
                maxLength={2000}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Character Image
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_IMAGE_TYPES.join(',')}
                onChange={handleFileSelect}
                disabled={uploadMedia.isPending}
                className="hidden"
              />

              {uploadedFile ? (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <img
                    src={getMediaUrl(uploadedFile.url)}
                    alt={uploadedFile.name}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{uploadedFile.name}</p>
                    <p className="text-xs text-green-600">Ready</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    title="Remove image"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadMedia.isPending}
                  loading={uploadMedia.isPending}
                >
                  {uploadMedia.isPending ? 'Uploading...' : 'Upload Image'}
                </Button>
              )}

              {uploadError && (
                <p className="text-sm text-red-600 mt-2">{uploadError}</p>
              )}
            </div>
          </div>

          <div>
            <CharacterAspects
              aspects={formData.aspects}
              onAdd={handleAddAspect}
              onRemove={handleRemoveAspect}
              canEdit={true}
            />
          </div>
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <div>
            {isEditing && onDelete && (
              <Button
                type="button"
                variant={confirmDelete ? 'danger' : 'secondary'}
                onClick={handleDelete}
                disabled={isSaving}
              >
                {confirmDelete ? 'Confirm Delete' : 'Delete'}
              </Button>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSaving}
              disabled={!formData.name.trim()}
            >
              {isEditing ? 'Save Changes' : 'Create Character'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
